import React, { useEffect, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import * as PropTypes from "prop-types";
import AmCharts from "../../chart/amcharts";
import { GRAPH_TYPE } from "../../../constants/trendGraph/trendGraphType";
import { useDispatch, useSelector } from "react-redux";
import { DASHBOARD_ADDITIONAL_ELECTRIC_TREND_TYPE } from "../constants";
import { updateChartLineName } from "../../../model/slice/chartSettingsSlice";
import { formatAdditionalElectricDataForCSV } from "../../../util/trendPage/csvDownload";
import { getCurrentFormattedDateTimeTextWithTimezoneForCSV } from "../../../util/timezone";
import { createCSV, createCsvDownload } from "../../../helpers/csvDownload/createCSV";
import { selectedTimezone } from "../../../model/slice/timezoneSlice";
import { fetchChannelNames } from "../../../api/channel";
import { getTrendDataForDashboard } from "../../trendPage/electric/useElectricDataList";

const onCSVButtonClickHandler = async ({
  graphData,
  graphConfig,
  vesselName,
  chartName,
  timezone,
}) => {
  const processedData = formatAdditionalElectricDataForCSV(graphData, graphConfig);
  const blob = await createCSV(processedData, ",");
  const fileName = `${vesselName}_additionalElectric_${chartName}_${getCurrentFormattedDateTimeTextWithTimezoneForCSV(timezone)}`;
  createCsvDownload({ blob, filename: fileName });
};

export const AdditionalElectricTrendWidget = ({
  vessel,
  selectedChart,
  index,
  startDate,
  endDate,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const timezone = useSelector(selectedTimezone);
  const [graphData, setGraphData] = useState([]);
  const [graphConfig, setGraphConfig] = useState([]);
  const [fetchedData, setFetchedData] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const graphName = DASHBOARD_ADDITIONAL_ELECTRIC_TREND_TYPE.find(
    (e) => e.chartType === selectedChart
  ).name;

  useEffect(() => {
    const fetchData = async ({ vesselId, chart, startDate, endDate }) => {
      const controller = new AbortController();
      const signal = controller.signal;
      setIsFetching(true);
      const channelLists = await fetchChannelNames(vesselId, chart);
      const data = await getTrendDataForDashboard(
        vesselId,
        chart,
        channelLists,
        startDate,
        endDate,
        signal
      );
      const taggedData = {
        [chart]: { data: data.data, graphConfig: { ...data.config, ...data.commonNames } },
      };
      setFetchedData(taggedData);
      setIsFetching(false);
    };
    fetchData({ vesselId: vessel.id, chart: selectedChart, startDate, endDate });
  }, [startDate, endDate]);

  useEffect(() => {
    if (fetchedData) {
      if (!fetchedData[selectedChart] || !fetchedData[selectedChart]?.graphConfig) return;
      dispatch(updateChartLineName(fetchedData[selectedChart].graphConfig));
      setGraphData(fetchedData[selectedChart].data);
      setGraphConfig(fetchedData[selectedChart].graphConfig);
    }
  }, [fetchedData]);

  return (
    <Box sx={{ height: "100%", padding: "8px 16px" }}>
      <h2 style={{ color: "white" }}>{t(graphName)}</h2>
      <button
        name="csvDownloadButton"
        style={{ visibility: "hidden" }}
        onClick={() => {
          onCSVButtonClickHandler({
            graphData,
            graphConfig,
            vesselName: vessel.name,
            chartName: t(graphName),
            timezone,
          });
        }}
      />
      <Box sx={{ height: "calc(100% - 30px)", minHeight: "100px" }}>
        {isFetching ? (
          <CircularProgress />
        ) : (
          <AmCharts
            graphData={graphData}
            graphDataDigital={[]}
            vesselId={vessel.id}
            chartType={selectedChart}
            type={GRAPH_TYPE.ELECTRIC}
            isComparison={false}
            vesselData={vessel}
            isMockPreview={false}
            expand={true}
            multipleGraphIndex={index}
            isVesselOverview={false}
            isDashboard={true}
            overviewTrendIdx={0}
          />
        )}
      </Box>
    </Box>
  );
};

AdditionalElectricTrendWidget.propTypes = {
  vessel: PropTypes.object.isRequired,
  selectedChart: PropTypes.string,
  index: PropTypes.number,
  startDate: PropTypes.object,
  endDate: PropTypes.object,
};
